import { ChangeEvent, Dispatch, useState } from 'react';
import { Modal, Button, Form, Alert, ListGroup, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAgentContext } from '../../context/agent';
import { useMutation, useQueryClient } from 'react-query';
import { uploadFiles } from '../../api/agent';
import { FaUpload, FaFile, FaTimes, FaPlus, FaExclamationTriangle } from 'react-icons/fa';

type Props = {
    show: boolean;
    setShow: Dispatch<React.SetStateAction<boolean>>;
}

const FileUploadModal = ({show, setShow} : Props) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [error, setError] = useState<string | null>(null);

    const { agentId} = useParams();
    const {getAgentById} = useAgentContext();
    const agent = getAgentById(agentId || "");
    
    const handleClose = () => setShow(false);

    function handleOnchange(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? [...e.target.files] : [];
    let selectedFilesCopy = [...selectedFiles]
    for(let file of files) {
        if(!selectedFilesCopy.includes(file)) selectedFilesCopy.push(file)
    }
    setSelectedFiles(selectedFilesCopy)
    e.preventDefault();
    }

    const queryClient = useQueryClient();
    const { mutateAsync: uploadFileFn, isLoading } = useMutation(
        uploadFiles,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['Files', agent?.id]);
                handleClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );

    const handleUpload = () => {
        if(selectedFiles.length === 0) {
            setError("Please select file(s) to upload");
            return;
        }
        if(!agent) {
            setError("Unexpected error. Please reload page and retry.")
            return;
        }

        const formData = new FormData();
        for(let file of selectedFiles) {
            formData.append('files', file, file.name);
        }
        formData.append("orgId", agent.organizationId);
        formData.append("id", agent.id);
        uploadFileFn({
            orgId: agent.organizationId,
            agentId: agent.id,
            formData
        });
    };

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            size="lg"
            contentClassName="bg-dark border-secondary"
        >
            <Modal.Header 
                closeButton
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <div className="d-flex align-items-center">
                    <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#28a745",
                            borderRadius: "8px"
                        }}
                    >
                        <FaUpload className="text-white" size={16} />
                    </div>
                    <Modal.Title className="text-light mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                        Upload Files
                    </Modal.Title>
                </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: "#1a1a1a" }}>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label className="text-light fw-semibold mb-2">
                            Document Files
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Select files to upload. Supported formats: PDF, DOC, DOCX, TXT, CSV.
                        </Form.Text>
                        <Form.Control
                            type="file"
                            multiple
                            onChange={handleOnchange}
                            className="bg-dark text-light border-secondary"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff"
                            }}
                        />
                    </Form.Group>
                </Form>
                
                {selectedFiles.length > 0 && (
                    <div className="mt-3">
                        <h6 className="text-light fw-semibold mb-3">
                            Selected Files ({selectedFiles.length})
                        </h6>
                        <ListGroup variant="flush">
                            {selectedFiles.map((file, index) => (
                                <ListGroup.Item 
                                    key={index}
                                    className="d-flex align-items-center justify-content-between"
                                    style={{
                                        backgroundColor: "#2d2d2d",
                                        borderColor: "#555",
                                        color: "#fff",
                                        padding: "12px 16px"
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FaFile className="me-3" style={{ color: "#28a745" }} size={16} />
                                        <div>
                                            <div className="text-light fw-medium">{file.name}</div>
                                            <small style={{ color: "#ccc" }}>
                                                {(file.size / 1024).toFixed(1)} KB
                                            </small>
                                        </div>
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() => {
                                            const newFiles = selectedFiles.filter((_, i) => i !== index);
                                            setSelectedFiles(newFiles);
                                        }}
                                        style={{ color: "#dc3545" }}
                                    >
                                        <FaTimes size={14} />
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </div>
                )}
                
                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FaExclamationTriangle className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    className="d-flex align-items-center"
                    style={{ borderColor: "#555", color: "#ccc" }}
                >
                    <FaTimes className="me-2" size={14} />
                    Cancel
                </Button>
                <Button 
                    variant="success" 
                    onClick={handleUpload}
                    disabled={isLoading || selectedFiles.length === 0}
                    className="d-flex align-items-center"
                    style={{ 
                        backgroundColor: "#28a745",
                        borderColor: "#28a745"
                    }}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <FaPlus className="me-2" size={14} />
                            Upload Files
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FileUploadModal;